import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Plus, User, Building, Mail, TrendingUp, FileText, DollarSign, Calendar, Trash2, AlertTriangle, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = ({
  user,
  onNavigateBack,
  onCreateNewCase,
  onOpenCase,
  onOpenPriceRequests,
  impersonateUsername,
  adminAuthHeader,
  onExitImpersonation,
}) => {
  const [cases, setCases] = useState([]);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalValuations: 0,
    totalCases: 0,
    completedCases: 0
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Store case ID to delete
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showAssessments, setShowAssessments] = useState(false); // Toggle for Recent Assessments - hidden by default
  const [showOpenCases, setShowOpenCases] = useState(false); // Toggle for Open Cases menu - hidden by default
  const [showCompletedAssessments, setShowCompletedAssessments] = useState(false); // Toggle for Completed Assessments menu - hidden by default

  // Probe the Blexbot session on open; a 401 triggers the global sign-in-again dialog.
  useEffect(() => {
    axios.post(`${API}/session/keep-alive`).catch(() => {});
  }, []);

  useEffect(() => {
    loadUserCases();
  }, [impersonateUsername]); // eslint-disable-line

  const loadUserCases = async () => {
    try {
      setLoading(true);
      let userCases;
      // Admin impersonation path: load the target user's cases via the
      // admin-only endpoint, authenticated by the admin's OTP-verified
      // Blexbot session (X-PV-Session).
      if (impersonateUsername) {
        const sessionToken = (() => {
          try { return sessionStorage.getItem('pv_session_token') || ''; } catch { return ''; }
        })();
        const response = await axios.get(
          `${API}/admin/users/${encodeURIComponent(impersonateUsername)}/dashboard`,
          { headers: { 'X-PV-Session': sessionToken } }
        );
        userCases = response.data?.cases || [];
        setImpersonatedUser(response.data?.user || null);
      } else {
        const response = await axios.get(`${API}/cases`);
        userCases = response.data;
      }

      setCases(userCases);

      // Calculate metrics
      const totalValuations = userCases.reduce((sum, c) => sum + (c.total_payout || 0), 0);
      const completedCases = userCases.filter(c => c.status === 'completed').length;

      setMetrics({
        totalValuations,
        totalCases: userCases.length,
        completedCases
      });
    } catch (error) {
      console.error('Error loading cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDeleteCase = async (caseId, caseNumber) => {
    setDeleteConfirm({ id: caseId, number: caseNumber });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      setDeleting(true);
      setError('');
      
      await axios.delete(`${API}/cases/${deleteConfirm.id}`);
      
      // Reload cases after deletion
      await loadUserCases();
      
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      setError('Failed to delete case. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  return (
    <div className="min-h-screen bg-white page-transition">
      {/* Admin impersonation banner */}
      {impersonateUsername && (
        <div
          data-testid="impersonation-banner"
          className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm flex items-center justify-between"
        >
          <div>
            <strong>Admin view</strong> — showing dashboard for user{" "}
            <strong>{impersonatedUser?.username || impersonateUsername}</strong>
            {impersonatedUser?.email && (
              <span className="text-amber-700"> ({impersonatedUser.email})</span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onExitImpersonation}
            data-testid="exit-impersonation-btn"
            className="border-amber-500 text-amber-800 hover:bg-amber-50 rounded-full"
          >
            Exit admin view
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
                alt="PriceValuator Logo" 
                className="h-48 object-contain"
              />
              <Button 
                variant="ghost" 
                onClick={onNavigateBack}
                className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {impersonateUsername ? "Back to Admin" : "Sign Out"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 page-transition">
            <Card className="w-full max-w-md mx-4 rounded-2xl shadow-2xl">
              <CardHeader className="bg-red-50 rounded-t-2xl">
                <CardTitle className="flex items-center text-red-800">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  Delete Case
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete case <strong>{deleteConfirm.number}</strong>? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={cancelDelete}
                    variant="outline"
                    disabled={deleting}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* User Profile Section */}
        <Card className="mb-8 rounded-2xl border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-cyan-100 p-4 rounded-full">
                  <User className="w-8 h-8 text-cyan-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-[#1E3A5F]">Welcome back, {user.username}!</CardTitle>
                  <div className="flex items-center space-x-4 mt-2 text-gray-600">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </div>
                    {user.company && (
                      <div className="flex items-center">
                        <Building className="w-4 h-4 mr-1" />
                        <span className="text-sm">{user.company}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Button
                onClick={onCreateNewCase}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create a new Valuation
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Quotation Request Entry — removed */}

        {/* Metrics Dashboard */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Total Valuations */}
          <Card className="rounded-2xl border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Valuations</p>
                  <p className="text-3xl font-bold text-green-800">{formatCurrency(metrics.totalValuations)}</p>
                </div>
                <div className="bg-green-100 p-4 rounded-full">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quantity of Valuations */}
          <Card className="rounded-2xl border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Assessments</p>
                  <p className="text-3xl font-bold text-blue-800">{metrics.totalCases}</p>
                </div>
                <div className="bg-blue-100 p-4 rounded-full">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Completed Cases */}
          <Card className="rounded-2xl border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Completed</p>
                  <p className="text-3xl font-bold text-purple-800">{metrics.completedCases}</p>
                </div>
                <div className="bg-purple-100 p-4 rounded-full">
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Assessments */}
        {(() => {
          const openCases = cases.filter((c) => String(c.status || "").toLowerCase() !== "completed");
          const completedCases = cases.filter((c) => String(c.status || "").toLowerCase() === "completed");

          const renderCaseRow = (caseItem) => (
            <div
              key={caseItem.id}
              className="p-4 border border-gray-200 rounded-xl hover:border-cyan-400 hover:shadow-md transition-all duration-300 bg-white"
            >
              <div className="flex items-center justify-between">
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => onOpenCase(caseItem.id)}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-[#1E3A5F] text-lg">{caseItem.title}</h3>
                    <Badge className={getStatusColor(caseItem.status)}>
                      {caseItem.status}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-1" />
                      <span>Case {caseItem.case_number}</span>
                      {caseItem.blexbot_request_number && (
                        <span
                          data-testid={`case-radicado-${caseItem.id}`}
                          className="ml-2 inline-flex items-center text-[10px] font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full"
                          title="Blexbot sourcing reference number"
                        >
                          ID # {caseItem.blexbot_request_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{formatDate(caseItem.created_at)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium">Items: {caseItem.total_items || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(caseItem.total_payout)}
                    </p>
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCase(caseItem.id, caseItem.case_number);
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          );

          const renderEmpty = (label) => (
            <div className="text-center py-10">
              <FileText className="w-14 h-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">{label}</p>
            </div>
          );

          return (
            <>
              {/* Recent Assessments — all cases */}
              <Card className="rounded-2xl border-2 border-gray-200 shadow-lg">
                <CardHeader 
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-2xl cursor-pointer hover:from-indigo-100 hover:to-purple-100 transition-all"
                  onClick={() => setShowAssessments(!showAssessments)}
                  data-testid="toggle-recent-assessments"
                >
                  <CardTitle className="text-xl text-[#1E3A5F] flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Recent Assessments
                      <span className="ml-2 text-xs font-semibold text-indigo-700 bg-white/70 rounded-full px-2 py-0.5">
                        {cases.length}
                      </span>
                    </div>
                    {showAssessments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </CardTitle>
                </CardHeader>
                {showAssessments && (
                  <CardContent className="p-6">
                    {loading ? (
                      <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading your claims...</p>
                      </div>
                    ) : cases.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">No claims yet</p>
                        <Button
                          onClick={onCreateNewCase}
                          variant="outline"
                          className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First Claim
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {cases.slice(0, 5).map(renderCaseRow)}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Open Cases */}
              <Card className="rounded-2xl border-2 border-gray-200 shadow-lg mt-6">
                <CardHeader 
                  className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-2xl cursor-pointer hover:from-amber-100 hover:to-orange-100 transition-all"
                  onClick={() => setShowOpenCases(!showOpenCases)}
                  data-testid="toggle-open-cases"
                >
                  <CardTitle className="text-xl text-[#1E3A5F] flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-amber-700" />
                      Open Cases
                      <span className="ml-2 text-xs font-semibold text-amber-800 bg-white/70 rounded-full px-2 py-0.5">
                        {openCases.length}
                      </span>
                    </div>
                    {showOpenCases ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </CardTitle>
                </CardHeader>
                {showOpenCases && (
                  <CardContent className="p-6" data-testid="open-cases-list">
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto"></div>
                      </div>
                    ) : openCases.length === 0 ? (
                      renderEmpty("No open cases — every assessment has been accepted.")
                    ) : (
                      <div className="space-y-4">
                        {openCases.map(renderCaseRow)}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Completed Assessments */}
              <Card className="rounded-2xl border-2 border-gray-200 shadow-lg mt-6">
                <CardHeader 
                  className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-2xl cursor-pointer hover:from-green-100 hover:to-emerald-100 transition-all"
                  onClick={() => setShowCompletedAssessments(!showCompletedAssessments)}
                  data-testid="toggle-completed-assessments"
                >
                  <CardTitle className="text-xl text-[#1E3A5F] flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-green-700" />
                      Completed Assessments
                      <span className="ml-2 text-xs font-semibold text-green-800 bg-white/70 rounded-full px-2 py-0.5">
                        {completedCases.length}
                      </span>
                    </div>
                    {showCompletedAssessments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </CardTitle>
                </CardHeader>
                {showCompletedAssessments && (
                  <CardContent className="p-6" data-testid="completed-assessments-list">
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
                      </div>
                    ) : completedCases.length === 0 ? (
                      renderEmpty("No completed assessments yet — accept every line in a case to mark it complete.")
                    ) : (
                      <div className="space-y-4">
                        {completedCases.map(renderCaseRow)}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default UserDashboard;
